import { Input } from "@/components/ui/input";
import { useDebounce } from "@/hooks/use-debounce";
import { useSiteSearchHits } from "@/hooks/use-site-search-hits";
import {
  mergeTitleRanges,
  type SiteSearchDocument,
  type SiteSearchHit,
} from "@/lib/site-search";
import { localizedPath } from "@/lib/routes";
import { cn } from "@/lib/utils";
import { ArrowRight, Search } from "lucide-react";
import {
  Fragment,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";

type SiteSearchProps = {
  locale: string;
  documents: SiteSearchDocument[];
  className?: string;
  initialQuery?: string;
  onActivate?: () => void;
  onNavigate?: () => void;
};

export function HighlightedTitle({ hit }: { hit: SiteSearchHit }) {
  const { title } = hit.document;
  const ranges = mergeTitleRanges(hit.titleMatches);
  if (ranges.length === 0) return <>{title}</>;

  let cursor = 0;
  const parts: ReactNode[] = [];
  ranges.forEach(([start, end], index) => {
    if (start > cursor) {
      parts.push(
        <Fragment key={`plain-${index}`}>
          {title.slice(cursor, start)}
        </Fragment>,
      );
    }
    parts.push(
      <mark
        key={`match-${index}`}
        className="rounded-sm bg-primary/15 px-0.5 text-current"
      >
        {title.slice(start, end + 1)}
      </mark>,
    );
    cursor = end + 1;
  });
  if (cursor < title.length) {
    parts.push(<Fragment key="plain-end">{title.slice(cursor)}</Fragment>);
  }
  return <>{parts}</>;
}

export default function SiteSearch({
  locale,
  documents,
  className,
  initialQuery = "",
  onActivate,
  onNavigate,
}: SiteSearchProps) {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const inputId = useId();
  const formRef = useRef<HTMLFormElement>(null);
  const [query, setQuery] = useState(initialQuery);
  const [debouncedQuery] = useDebounce(query, 180);
  const [expanded, setExpanded] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const canSearch = query.trim().length >= 2;
  const open = expanded && canSearch;
  const { results, ready } = useSiteSearchHits(
    documents,
    debouncedQuery,
    8,
    expanded || Boolean(initialQuery),
  );

  useEffect(() => {
    setQuery(initialQuery);
  }, [initialQuery]);

  useEffect(() => {
    setActiveIndex((current) =>
      results.length === 0 ? -1 : Math.min(current, results.length - 1),
    );
  }, [results.length]);

  const navigateTo = (href: string) => {
    setExpanded(false);
    setActiveIndex(-1);
    navigate(href);
    onNavigate?.();
  };

  const submitSearch = () => {
    const value = query.trim();
    if (value.length < 2) return;
    navigateTo(
      `${localizedPath("search", locale)}?q=${encodeURIComponent(value)}`,
    );
  };

  return (
    <form
      ref={formRef}
      role="search"
      className={cn("relative", className)}
      onSubmit={(event) => {
        event.preventDefault();
        if (activeIndex >= 0 && results[activeIndex]) {
          navigateTo(results[activeIndex].document.href);
          return;
        }
        submitSearch();
      }}
      onFocusCapture={() => {
        onActivate?.();
        setExpanded(true);
      }}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setExpanded(false);
          setActiveIndex(-1);
        }
      }}
    >
      <label htmlFor={inputId} className="sr-only">
        {t("search.label")}
      </label>
      <Search
        aria-hidden="true"
        className="pointer-events-none absolute left-4 top-1/2 z-10 size-5 -translate-y-1/2 text-muted-foreground"
      />
      <Input
        id={inputId}
        role="combobox"
        type="search"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setExpanded(true);
          setActiveIndex(-1);
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            setExpanded(false);
            setActiveIndex(-1);
            return;
          }
          if (!open || results.length === 0) return;
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setActiveIndex((current) => (current + 1) % results.length);
          } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setActiveIndex((current) =>
              current <= 0 ? results.length - 1 : current - 1,
            );
          }
        }}
        placeholder={t("search.placeholder")}
        autoComplete="off"
        aria-autocomplete="list"
        aria-controls={open ? `${inputId}-results` : undefined}
        aria-expanded={open}
        aria-activedescendant={
          open && activeIndex >= 0
            ? `${inputId}-option-${activeIndex}`
            : undefined
        }
        className="h-14 rounded-2xl bg-background/95 pl-12 pr-5 text-base shadow-sm"
      />

      <span className="sr-only" role="status" aria-live="polite">
        {open ? t("search.suggestions_count", { count: results.length }) : ""}
      </span>

      {open && (
        <ul
          id={`${inputId}-results`}
          role="listbox"
          aria-label={t("search.suggestions")}
          className="absolute left-0 right-0 top-full z-[70] mt-2 max-h-[min(32rem,70vh)] overflow-y-auto rounded-2xl border border-border bg-popover p-1.5 shadow-2xl"
        >
          {!ready ? (
            <li className="px-3 py-3 text-sm text-muted-foreground">
              {t("search.loading")}
            </li>
          ) : results.length > 0 ? (
            <>
              {results.map((hit, resultIndex) => (
                <li
                  key={hit.document.id}
                  id={`${inputId}-option-${resultIndex}`}
                  role="option"
                  aria-selected={activeIndex === resultIndex}
                >
                  <Link
                    to={hit.document.href}
                    onMouseEnter={() => setActiveIndex(resultIndex)}
                    onClick={() => {
                      setExpanded(false);
                      setActiveIndex(-1);
                      onNavigate?.();
                    }}
                    className={cn(
                      "grid grid-cols-[1fr_auto] gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      activeIndex === resultIndex
                        ? "bg-accent text-accent-foreground"
                        : "hover:bg-accent/70",
                    )}
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-semibold">
                        <HighlightedTitle hit={hit} />
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                        {hit.document.meta ||
                          t(`search.kind.${hit.document.kind}`)}
                      </span>
                    </span>
                    <ArrowRight
                      aria-hidden="true"
                      className="mt-1 size-4 shrink-0 text-primary"
                    />
                  </Link>
                </li>
              ))}
              <li className="mt-1 border-t border-border pt-1">
                <button
                  type="button"
                  onClick={submitSearch}
                  className="block w-full rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-primary transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {t("search.all_results")}
                </button>
              </li>
            </>
          ) : (
            <li className="px-3 py-3 text-sm text-muted-foreground">
              {t("search.no_results")}
            </li>
          )}
        </ul>
      )}
    </form>
  );
}
